import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import type { CommentaryCreateInterface } from '@zaoblako/shared/modules/messenger/dtos/commentary/commentary-create.dto';
import type { FileFindInterface } from '@zaoblako/shared/modules/storage/dtos/file-find.dto';
import { FileService } from '@zaoblako/web/core/services/file.service';
import { CommentUtilsService } from '@zaoblako/web/shared/comments/services/comment-utils.service';
import { FileUploaderComponent } from '@zaoblako/web/shared/components/file-uploader/file-uploader.component';
import { FilesListComponent } from '@zaoblako/web/shared/components/files-list/files-list.component';
import { EditorComponent } from '@zaoblako/web/shared/dumbs/editor/editor.component';
import { TruncatePipe } from '@zaoblako/web/shared/pipes/truncate.pipe';
import { ButtonModule } from 'primeng/button';

@Component({
  standalone: true,
  selector: 'crm-comment-editor',
  templateUrl: './comment-editor.component.html',
  styleUrls: ['./comment-editor.component.scss'],
  imports: [CommonModule, EditorComponent, FilesListComponent, TruncatePipe, ButtonModule, FileUploaderComponent],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CommentEditorComponent {
  public comment: CommentaryCreateInterface = {};
  @Input() public isShowSendButton = true;
  @Input() public height: string;

  public showUserList = false;

  private previousText = '';
  private currentAsperandIndex: number;

  @Output() public createComment = new EventEmitter<CommentaryCreateInterface>();

  @ViewChild('editor') public editor: EditorComponent;

  public constructor(
    private readonly commentUtilsService: CommentUtilsService,
    private readonly fileService: FileService,
    private readonly cdRef: ChangeDetectorRef
  ) {}

  public saveComment(): void {
    this.commentUtilsService.parseTimeWorkInComment(this.comment);
    this.createComment.emit(this.comment);
  }

  public onPaste(event: ClipboardEvent) {
    this.fileService.getPastedImage(event).subscribe((image) => {
      if (image) {
        this.comment.files.push(image);
        this.cdRef.markForCheck();
        if (!this.isShowSendButton) {
          this.createComment.emit(this.comment);
        }
      }
    });
  }

  public changeTextHandler(event: any) {
    if (this.commentUtilsService.isAsperandInserted(event)) {
      this.currentAsperandIndex = this.commentUtilsService.findCurrentAsperandIndex(event.htmlValue, this.previousText);
      this.showUserList = true;
    } else {
      this.showUserList = false;
    }
    this.previousText = event.htmlValue || '';
    if (!this.isShowSendButton) {
      this.createComment.emit(this.comment);
    }
  }

  public attachFiles(files: FileFindInterface[]): void {
    if (!this.comment?.files) {
      this.comment.files = [];
    }
    this.comment.files.push(...files);
    if (!this.isShowSendButton) {
      this.createComment.emit(this.comment);
    }
  }

  public deleteFile(file: FileFindInterface): void {
    this.comment.files.splice(this.comment.files.indexOf(file), 1);
    if (!this.isShowSendButton) {
      this.createComment.emit(this.comment);
    }
  }

  public resetCommentForm(): void {
    this.comment = { files: [], mentions: [], text: '' };
    this.showUserList = false;
    if (!this.isShowSendButton) {
      this.createComment.emit(this.comment);
    }
  }

  public dropFiles(event: DragEvent) {
    event.stopPropagation();
    event.preventDefault();

    const files = [...(event.dataTransfer.files as unknown as any[])];
    if (files.length > 0) {
      this.fileService.upload(files as any).subscribe((resFiles) => {
        if (!this.comment.files) {
          this.comment.files = [];
        }
        this.comment.files.push(...resFiles);
        this.comment.files = [...this.comment.files];
        this.cdRef.markForCheck();
      });
    }
  }

  public toFiles(values: any[]): FileFindInterface[] {
    return values as FileFindInterface[];
  }
}
